import * as localForage from 'localforage';
import * as memoryStorageDriver from 'localforage-memoryStorageDriver';
import isIndexedDBValid from './indexed-db/isIndexedDBValid';
import getIDB from './indexed-db/idb';
import {
  getCoreItemKey,
  getInitialSetupItemKey,
  getFinalSetupItemKey,
  getLastFetchedBackgroundDataStepItemKey,
  getLastFetchedMonthIndexItemKey,
  getLastUpdatedAtItemKey
} from './meta-table-helper';

const dbName = 'planner';
let initialVerificationDone = false;

let baseLocalForageConfig = {
  name: dbName,
  driver: [localForage.INDEXEDDB]
};

export const getItemsTable = (goalUID) =>
  localForage.createInstance({
    name: dbName,
    storeName: `${goalUID}-items`,
    description: `A planner items table for ${goalUID}`
  });

export const getDaysTable = (goalUID) =>
  localForage.createInstance({
    name: dbName,
    storeName: `${goalUID}-days`,
    description: `A planner days table for ${goalUID}`
  });

export const getMetaTable = () =>
  localForage.createInstance({
    name: dbName,
    storeName: 'meta',
    description: 'A planner meta table'
  });

export const clearMetaKeysForGoal = (goalUID) => {
  const metaTable = getMetaTable();
  const initialSetupKey = getInitialSetupItemKey(goalUID);
  const finalSetupKey = getFinalSetupItemKey(goalUID);
  const coreKey = getCoreItemKey(goalUID);
  const lastFetchedBackgroundDataStepKey =
    getLastFetchedBackgroundDataStepItemKey(goalUID);
  const lastFetchedMonthIndexKey = getLastFetchedMonthIndexItemKey(goalUID);
  const lastUpdatedAtKey = getLastUpdatedAtItemKey(goalUID);
  return Promise.all([
    metaTable.removeItem(coreKey),
    metaTable.removeItem(initialSetupKey),
    metaTable.removeItem(finalSetupKey),
    metaTable.removeItem(lastFetchedBackgroundDataStepKey),
    metaTable.removeItem(lastFetchedMonthIndexKey),
    metaTable.removeItem(lastUpdatedAtKey)
  ]);
};

export const makeDummyIDBCall = () => {
  // Took work-around from https://bugs.webkit.org/show_bug.cgi?id=226547
  // and https://bugs.chromium.org/p/chromium/issues/detail?id=242115
  if (isIndexedDBValid()) {
    const idb = getIDB();
    if (idb) {
      // trying to establish connection to a random db
      const dbRequest = idb.open('random-dummy');
      const onEvent = (e) => {
        e.target?.result?.close();
      };
      dbRequest.onblocked = onEvent;
      dbRequest.onsuccess = onEvent;
      dbRequest.onerror = onEvent;
      dbRequest.onupgradeneeded = onEvent;
    }
  }
};

export const isStorageAvailable = async () => {
  if (initialVerificationDone) {
    return true;
  }
  try {
    makeDummyIDBCall();
    localForage.config(baseLocalForageConfig);
    await localForage.ready();
    initialVerificationDone = true;
    return true;
  } catch {
    await localForage.defineDriver(memoryStorageDriver);
    // eslint-disable-next-line no-underscore-dangle
    const driver = memoryStorageDriver._driver;
    await localForage.setDriver(driver);
    baseLocalForageConfig = {
      ...baseLocalForageConfig,
      driver: [driver]
    };
    return false;
  }
};
